/**
 * Shared currency-input helpers for Delivery Portal forms.
 *
 * Goals:
 * - Always allow decimal entry (cents)
 * - Preserve a trailing "." while the user is typing (e.g. "$12.")
 * - Cap visible fraction digits at 2
 * - Normalize to "X.XX" on blur
 */

/** Strip formatting; keep digits and a single decimal point. */
export function parseCurrencyInput(value) {
  const cleaned = String(value ?? '').replace(/[^\d.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot === -1) return cleaned;
  return (
    cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '')
  );
}

/**
 * Display value for a controlled currency text input.
 * Returns "" for empty; otherwise a "$"-prefixed string that may include
 * a trailing decimal while typing.
 */
export function formatCurrencyInput(value) {
  const numericValue = parseCurrencyInput(value);
  if (!numericValue) return '';
  // Preserve a leading decimal while typing (e.g. ".5" → "$.5").
  if (numericValue === '.') return '$.';

  const parts = numericValue.split('.');
  let decimalPart = '';
  if (parts.length > 1) {
    // Keep the trailing "." even when no fraction digits yet; cap at 2 digits.
    decimalPart = `.${(parts[1] || '').slice(0, 2)}`;
  }

  const integerPart = (parts[0] || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `$${integerPart}${decimalPart}`;
}

/** Normalize a currency field to exactly two decimal places (or clear). */
export function normalizeCurrencyOnBlur(value) {
  const cleaned = parseCurrencyInput(value);
  if (!cleaned || cleaned === '.') return '';
  const num = parseFloat(cleaned);
  return Number.isNaN(num) ? '' : num.toFixed(2);
}
